import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Grid, MenuItem, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import {
  notiError,
  notiSuccess,
} from "../../component/notification/notifications";
import Cloudupload from "../../assets/icon/cloudupload";
import * as Style from "./handbook.style";
import ButtonCustom from "../../component/button/buttonCustom";
import FileuploadApi from "../../api/uploadfile/upload.api";
import FilterSelect from "../../component/select/filterSelect";
import { saveAs } from "file-saver";
import Loading from "../../component/loading/loading";
import { getFileExtension } from "../../utils/fileTypeDownload";
import { getProfile, getToken } from "../../utils/app.utils";
import { decode } from "js-base64";
import { useLocation } from "react-router-dom";

const HandBookComponent = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const handbookType = queryParams.get("handbook-type");

  const [localFile, setLocalFile] = useState<File | null>(null);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);

  const token = getToken();
  const profile: any = token ? JSON.parse(getProfile() || "{}") : "{}";

  const handleFileDrop = useCallback((acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];

    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();

    if (fileExtension === "pdf") {
      setLocalFile(selectedFile);
    } else {
      notiError("เกิดข้อผิดพลาด", "รองรับเฉพาะไฟล์ PDF เท่านั้น", null, null);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileDrop,
  });

  const handlePreview = (fileItem: any) => {
    if (fileItem instanceof File) {
      window.open(URL.createObjectURL(fileItem), "_blank");
    } else if (fileItem?.mimetype === "application/pdf") {
      window.open(
        `${process.env.REACT_APP_FILE_URL}${fileItem.filePath}${fileItem.fileName}`,
        "_blank"
      );
    }
  };

  const handleSubmit = async () => {
    if (!localFile) {
      notiError("กรุณาเลือกไฟล์", "", null, null);
      return;
    }

    const formData = new FormData();
    formData.append("refId", selectedType.toString());
    formData.append("fileType", "hand_book");
    formData.append("file", localFile);

    try {
      await FileuploadApi.uploadHandbook(formData);
      notiSuccess("อัปโหลดสำเร็จ", "", "", null);
      setLocalFile(null); // Clear local file
      getFile(selectedType); // Refresh uploaded file from server
    } catch (error) {
      notiError("อัปโหลดไม่สำเร็จ", "", null, null);
    }
  };

  const getFile = async (type: number) => {
    try {
      setUploadedFile(null);
      const res = await FileuploadApi.getUploadHandbook(type ?? selectedType);

      if (res.status === 200 && res.data?.length > 0) {
        setTimeout(() => {
          setUploadedFile(res.data[0]);
        }, 1000);
      } else {
        setUploadedFile(null);
      }
    } catch (error) {
      notiError("ไม่สามารถดึงข้อมูลไฟล์ได้", "", null, null);
    }
  };

  const downloadFile = async (id: number) => {
    try {
      const condition = { refId: selectedType };
      const res = await FileuploadApi.downloadHandbook(id, condition);

      if (res.status < 300) {
        const response = res.data;

        // Get the content type from the server's response headers
        const contentType = res.headers["content-type"];

        // Create a Blob with the content type from the response
        const blob = new Blob([response], { type: contentType });

        // Extract file extension based on content type
        const fileExtension = getFileExtension(contentType || "");

        // Generate a filename with the correct extension (you can also modify this to use a custom name)
        const name_file = `คู่มือ_${new Date().getTime()}`;
        const fileName = `${name_file}.${fileExtension}`;

        // Save the file
        saveAs(blob, fileName);
        setLoading(false);
      }
    } catch (error) {
      notiError("ไม่สามารถดึงข้อมูลไฟล์ได้", "", null, null);
    }
  };

  useEffect(() => {
    if (!profile) return;

    getFile(handbookType ? +handbookType : 1);

  }, [profile, handbookType]);


  useEffect(() => {
    setLocalFile(null);
  }, [selectedType]);

  return (
    <>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <Loading show={loading} type="fullLoading" />
        </div>
      )}
      {profile && JSON.parse(decode(profile || "") || "")?.roleId !== 4 ? (
        <Style.MainContainer>
          <Grid className="my-2" item xs={12}>
            {/* File Upload Box */}
            <Style.Boxupload>
              <Style.Boxdash {...getRootProps()}>
                <input {...getInputProps()} />
                <Box
                  sx={{
                    flexDirection: "column",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Cloudupload />
                  <Typography
                    sx={{
                      marginTop: "24px",
                      fontSize: "18px",
                      fontFamily: "kanit",
                    }}
                  >
                    แนบไฟล์เอกสาร PDF
                  </Typography>
                </Box>
              </Style.Boxdash>
            </Style.Boxupload>

            {/* Display local file */}
            {localFile && (
              <Grid item lg={10} xs={12} className="my-2">
                <Style.Array>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box>{localFile.name}</Box>
                    <Box>
                      <VisibilityIcon
                        onClick={() => handlePreview(localFile)}
                        sx={{ cursor: "pointer", marginRight: "10px" }}
                      />
                    </Box>
                  </Box>
                </Style.Array>
              </Grid>
            )}

            {/* Display uploaded file */}
            {!localFile && uploadedFile && (
              <Grid item lg={10} xs={12} className="my-2">
                <Style.Array>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box>{uploadedFile.fileName}</Box>
                    <Box>
                      <VisibilityIcon
                        onClick={() => handlePreview(uploadedFile)}
                        sx={{ cursor: "pointer", marginRight: "10px" }}
                      />
                      <CloudDownloadIcon
                        onClick={() => downloadFile(uploadedFile.fileId)}
                        sx={{ cursor: "pointer", marginRight: "10px" }}
                      />
                    </Box>
                  </Box>
                </Style.Array>
              </Grid>
            )}

            {/* File type selection */}
            <FilterSelect
              onchange={(event) => {
                setSelectedType(Number(event.target.value))
                getFile(event.target.value)
              }}
              label="ประเภท"
              selectId="select-hand-book"
              value={selectedType}
              labelId="label-hand-book"
              options={[
                <MenuItem key="0" value={0}>
                  คู่มือผู้ดูแลระบบ
                </MenuItem>,
                <MenuItem key="1" value={1}>
                  คู่มือลูกค้า
                </MenuItem>,
              ]}
            />
            <br />
            {/* Upload button */}
            <ButtonCustom
              textButton="อัปโหลด"
              style={{ width: "100%" }}
              btnStyle={{ marginTop: 4 }}
              onClick={handleSubmit}
            />
          </Grid>
        </Style.MainContainer>
      ) : (
        <Style.MainContainer onClick={() => downloadFile(uploadedFile.fileId)}>
          {!localFile && uploadedFile && (
            <Grid className="my-2" item xs={12}>
              {/* File Upload Box */}
              <Style.Boxupload>
                <Style.BoxNone>
                  <Box
                    sx={{
                      flexDirection: "column",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Typography
                      sx={{
                        marginTop: "24px",
                        fontSize: "18px",
                        fontFamily: "kanit",
                      }}
                    >
                      <Box>{uploadedFile.fileName}</Box>
                    </Typography>
                    <CloudDownloadIcon
                      sx={{ cursor: "pointer", marginRight: "10px" }}
                    />
                  </Box>
                </Style.BoxNone>
              </Style.Boxupload>
            </Grid>
          )}
        </Style.MainContainer>
      )}
    </>
  );
};

export default HandBookComponent;
